import { ref } from 'vue'

import { useQueryClient } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'

import { type AiConversationsResponse, aiKeys } from '@/api/v2/queries'
import { type AiMessage, useAiStore } from '@/stores/ai'

import type { StreamEvent } from './useAiStream'
import { useAiStream } from './useAiStream'

export function useAiChat() {
  const aiStore = useAiStore()
  const queryClient = useQueryClient()
  const { messages, isStreaming, streamingPhase, currentConversationId, selectedProvider } =
    storeToRefs(aiStore)
  const { streamChat } = useAiStream()

  const abortController = ref<AbortController | null>(null)

  async function sendMessage(text: string) {
    if (!text.trim() || isStreaming.value) return

    const userMessage: AiMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text.trim(),
      timestamp: new Date().toISOString(),
    }
    aiStore.addMessage(userMessage)

    // Track the current assistant message for text_delta appends.
    // When tool messages appear between text chunks, a new assistant
    // message is created so text renders below the tool cards.
    let assistantMsg: AiMessage | null = null

    function getAssistantMsg(): AiMessage {
      const lastMsg = messages.value.at(-1)
      if (assistantMsg && lastMsg === assistantMsg) return assistantMsg

      aiStore.addMessage({
        id: crypto.randomUUID(),
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
      })
      assistantMsg = messages.value.at(-1)!
      return assistantMsg
    }

    isStreaming.value = true
    streamingPhase.value = 'thinking'
    abortController.value = new AbortController()

    await streamChat({
      message: text.trim(),
      conversationId: currentConversationId.value,
      provider: selectedProvider.value,
      signal: abortController.value.signal,
      onEvent: (event: StreamEvent) => {
        switch (event.type) {
          case 'text_delta':
            streamingPhase.value = 'generating'
            getAssistantMsg().content += event.content ?? ''
            break
          case 'tool_call':
            streamingPhase.value = 'tool_calling'
            aiStore.addMessage({
              id: event.event_id,
              role: 'tool',
              content: '',
              toolName: event.tool_name,
              toolInput: event.tool_input,
              timestamp: new Date().toISOString(),
            })
            break
          case 'tool_result': {
            // Find matching tool_call message and update it with the result
            const toolMsg = [...messages.value]
              .reverse()
              .find(m => m.role === 'tool' && m.toolName === event.tool_name && !m.toolResult)
            if (toolMsg) {
              toolMsg.toolResult = event.result
            }
            break
          }
          case 'done':
            if (event.conversation_id && !currentConversationId.value) {
              currentConversationId.value = event.conversation_id
            }
            if (event.title && event.conversation_id) {
              queryClient.setQueryData<AiConversationsResponse>(aiKeys.conversations(), old => {
                if (!old) return old
                return {
                  ...old,
                  conversations: old.conversations.map(c =>
                    c.id === event.conversation_id ? { ...c, title: event.title! } : c,
                  ),
                }
              })
              queryClient.invalidateQueries({ queryKey: aiKeys.conversations() })
            }
            break
        }
      },
      onError: (error: Error) => {
        if (error.name === 'AbortError') return
        getAssistantMsg().content += `\n\n_Error: ${error.message}_`
      },
      onDone: () => {
        isStreaming.value = false
        streamingPhase.value = null
        abortController.value = null
      },
    })
  }

  function stopStreaming() {
    abortController.value?.abort()
    isStreaming.value = false
    streamingPhase.value = null
    abortController.value = null
  }

  async function regenerateLastResponse() {
    const lastUserIdx = messages.value.findLastIndex(m => m.role === 'user')
    if (lastUserIdx < 0) return

    const text = messages.value[lastUserIdx].content
    aiStore.removeMessagesFromIndex(lastUserIdx)
    await sendMessage(text)
  }

  return {
    messages,
    isStreaming,
    streamingPhase,
    sendMessage,
    stopStreaming,
    regenerateLastResponse,
  }
}
